import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AuthService from '../services/auth.service';

interface LoginData {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const [loginData, setLoginData] = useState<LoginData>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Set document title
  useEffect(() => {
    document.title = 'Login - MRS, UoR, Efac';
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData({
      ...loginData,
      [name]: value,
    });
  };

  // Check for messages from signup or other pages
  useEffect(() => {
    const state = location.state as { message?: string } | null;
    if (state && state.message) {
      setSuccessMessage(state.message);
      setError(null);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await AuthService.login(loginData.email, loginData.password);
      
      // Check user role and redirect accordingly
      const user = response.user;
      
      // Check for redirect after login
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      
      // Check if user is admin (role_id 2 is admin, 1 is regular user)
      if (user.role_id == 2 || user.role_id == 5 ) {
        navigate('/admin');
      } else if (user.role_id === 3) {
        navigate('/network-admin-dashboard'); 
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex">
      {/* Left Side - Branding and Information */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-white">
        <div className="flex flex-col justify-between px- py-12 w-full max-w-2xl mx-auto">
          
          {/* Header Section */}
          <div className=" ">
            {/* System Title */}
            <div className="flex items-center">
    
              <div>
                <h2 className="text-3xl font-light text-gray-900 leading-tight">
                  Maintenance, Requests and Complaints Management System
                </h2>
                
              </div>
            </div>
            {/* University Branding */}
            <div className="flex items-center mb-8 mt-8">
              <img 
                src="/images/logo-uor.png" 
                alt="University Logo" 
                className="h-14 w-14 object-contain mr-4"
              />
              <div className="text-center">
                <h1 className="text-2xl font-medium text-gray-900">
                  University of Ruhuna
                  </h1>
                  <p className="text-base text-gray-600">Faculty of Engineering</p>
                  </div>

                        <img 
                src="/images/logo-eng.png" 
                alt="Faculty Logo" 
                className="h-20 w-16 scale-75 mr-4"
              />
              
            </div>

          </div>



          {/* Footer */}
          <div className="mt-16 pt-6 border-t border-gray-100">
            <div className="flex justify-between items-center text-sm text-gray-500">
              <span>© 2026 University of Ruhuna</span>
              <a
                href="https://scorpion-xweb.vercel.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
              >
                Powered by Scorpion X
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col bg-gray-50">
        <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          
          {/* Mobile Header */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img 
                src="/images/logo-uor.png" 
                alt="University Logo" 
                className="h-10 w-10 object-contain mr-3"
              />
              <img 
                src="/images/logo-eng.png" 
                alt="Faculty Logo" 
                className="h-10 w-10 object-contain"
              />
            </div>
            <h1 className="text-lg font-medium text-gray-900">
              University of Ruhuna
            </h1>
            <p className="text-sm text-gray-600">Maintenance, Requests and Complaints Management System</p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            
            {/* Form Header */}
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-2xl font-normal text-gray-900">Sign in</h2>
              <p className="text-sm text-gray-600 mt-2">
                Use your account to continue
              </p>
            </div>

            {/* Form Body */}
            <div className="px-8 py-6">
              
              {/* Alert Messages */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                  {successMessage}
                </div>
              )}

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={loginData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors"
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={loginData.password}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-10 transition-colors"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {showPassword ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        )}
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600">Remember me</span>
                  </label>
                  <Link 
                    to="/reset/password" 
                    className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2.5 px-4 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-sm"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Signing in...
                    </div>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </form>

              {/* Sign Up Section */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-center text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link 
                    to="/signup" 
                    className="text-blue-600 hover:text-blue-500 font-medium"
                  >
                    Create account
                  </Link>
                </p>
                <p className="text-center text-sm text-gray-600 mt-3">
                  Need assistance?{' '}
                  <Link
                    to="/help"
                    className="text-blue-600 hover:text-blue-500 font-medium"
                  >
                    Help &amp; Support
                  </Link>
                </p>
              </div>

              {/* Download Mobile App */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <a
                  href="https://scorpion-xweb.vercel.app/uor_req_portal"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded border border-gray-300 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700 transition-colors"
                >
                  <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Download Mobile App
                </a>
              </div>
            </div>
          </div>

        </div>
        </div>

        {/* Mobile Footer - pinned to bottom */}
        <div className="lg:hidden px-8 pb-6 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center text-sm text-gray-500">
            <span>© 2026 University of Ruhuna</span>
            <a
              href="https://scorpion-xweb.vercel.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              Powered by Scorpion X
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;